import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import EditForm from 'components/editForm/EditForm';
import Check from 'components/icon/icons/green_check.svg';
import {
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    StateInterface,
    ValuesStringInterface,
} from 'components/interfaces/GeneralInterface';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import H3 from 'components/typography/H3';
import {
    FieldTypeApiEnum,
    MemberRegistrationApiInterface,
} from 'submodules/api_middleware';
import { FormResponseData, UserService } from 'services/user.service';
import { getRoute } from 'constants/routes';
import Icon from 'components/icon/Icon';
import H2 from 'components/typography/H2';
import ButtonLoader from 'components/loading/ButtonLoader';
import {
    OnSubmitData,
    OnSuccessData,
} from 'components/interfaces/EditFormInterface';
import { TNamespaceEnum } from 'types/types';
import styles from './SignUpActivation.module.scss';

const intervalSeconds = [30, 60, 300];
let outerIntervalIndex = 0;

interface Props {
    getRequestData: () => MemberRegistrationApiInterface;
    photo?: string;
    goBack: (values: ValuesStringInterface) => void;
}

const EMAIL_ACTIVATION = 'email_activation';

enum ResentStatesEnum {
    'HIDE' = 'HIDE',
    'SHOW' = 'SHOW',
    'SENT' = 'SENT',
    'LOADING' = 'LOADING',
}

let setFormFields: (values: StateInterface) => void;
let getFormFields: () => StateInterface;
let repeatingTimeout: number;
let successTimeout: number;

const SignUpActivation = ({
    goBack,
    getRequestData,
    photo,
}: Props): ReactElement => {
    const router = useRouter();
    const { t } = useTranslation();
    const [successMessage, setSuccess] = useState<string>(null);
    const [resentState, setResentState] = useState<ResentStatesEnum>(
        ResentStatesEnum.HIDE
    );
    const [allowTimeouts, setAllowTimeouts] = useState<boolean>(true);
    const fields = useMemo<GroupEditFieldInterface[]>(
        (): GroupEditFieldInterface[] => getFieldWithLocalization(),
        []
    );

    // Check the email verification code is the same as on application.
    const verifyApplicationContact: OnSubmitData = async (values) => {
        const data: MemberRegistrationApiInterface = {
            ...getRequestData(),
            verification: {
                email: values.email_activation,
            },
        };
        if (photo) {
            data.person.photo = photo;
        }
        data.payment_url = `${location.origin}/application-payment`;
        return await new UserService().register(data);
    };

    const onMemberApplicationSubmitted: OnSuccessData = async (
        _,
        __,
        response: FormResponseData
    ) => {
        setSuccess(
            getTranslation(
                t,
                TNamespaceEnum.signup,
                'registrationSuccessYoungMessage'
            )
        );

        const applicationId = response.data.id;
        const discipline = response.data.discipline;
        setTimeout(
            () =>
                router.push(
                    getRoute('/application-payment', [], null, {
                        applicationId,
                        discipline,
                    })
                ),
            5200
        );
    };

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setResentState(ResentStatesEnum.SHOW);
            outerIntervalIndex < 2 && (outerIntervalIndex += 1);
        }, intervalSeconds[parseInt(`${outerIntervalIndex}`)] * 1000);
        return () => {
            timeout && window.clearTimeout(timeout);
            repeatingTimeout && window.clearTimeout(repeatingTimeout);
            successTimeout && window.clearTimeout(successTimeout);
        };
    }, []);

    const handleResendActivation = async (
        name: 'email',
        resetFieldName: string
    ): Promise<void> => {
        const data: MemberRegistrationApiInterface = getRequestData();
        setResentState(ResentStatesEnum.LOADING);

        setFormFields({ ...getFormFields(), [resetFieldName]: undefined });

        await new UserService().registerCheckResend({
            type: name,
            value: data.person[`${name}` as 'email'],
        });
        setResentState(ResentStatesEnum.SENT);

        successTimeout = window.setTimeout(
            () => setResentState(ResentStatesEnum.HIDE),
            10000
        );

        if (allowTimeouts) {
            repeatingTimeout = window.setTimeout(
                () => setResentState(ResentStatesEnum.SHOW),
                intervalSeconds[parseInt(`${outerIntervalIndex}`)] * 1000
            );
            if (outerIntervalIndex < 2) {
                outerIntervalIndex += 1;
            } else {
                setAllowTimeouts(false);
            }
        }
    };

    const getMutateFieldsCb = (
        setValues: (values: StateInterface) => void,
        getValues: () => StateInterface
    ) => {
        setFormFields = setValues;
        getFormFields = getValues;
    };

    if (successMessage) {
        return (
            <div className={styles.successText}>
                <Icon icon={Check} className={styles.icon} />
                <H2>
                    {getTranslation(t, TNamespaceEnum.signup, 'successMessage')}
                </H2>
                <div>
                    <strong>{successMessage}</strong>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className={styles.title}>
                {getTranslation(
                    t,
                    TNamespaceEnum.signup,
                    'activationSentPartOne'
                )}
            </div>
            <div className={styles.sendActivation}>
                {resentState !== ResentStatesEnum.HIDE &&
                    (resentState === ResentStatesEnum.SENT ? (
                        <div>
                            {getTranslation(
                                t,
                                TNamespaceEnum.signup,
                                'activationResendSuccessful'
                            )}
                        </div>
                    ) : resentState === ResentStatesEnum.LOADING ? (
                        <div className={styles.buttonLoaderWrapper}>
                            <ButtonLoader isDark />
                        </div>
                    ) : (
                        <>
                            <div>
                                {getTranslation(
                                    t,
                                    TNamespaceEnum.signup,
                                    'activationMissing'
                                )}
                            </div>
                            <div
                                onClick={() =>
                                    handleResendActivation(
                                        'email',
                                        EMAIL_ACTIVATION
                                    )
                                }
                                className={styles.sendActivationLink}
                            >
                                {getTranslation(
                                    t,
                                    TNamespaceEnum.signup,
                                    'activationResendEmail'
                                )}
                            </div>
                        </>
                    ))}
            </div>
            <H3>
                {getTranslation(
                    t,
                    TNamespaceEnum.signup,
                    'activationSentPartTwo'
                )}
            </H3>
            <EditForm
                fields={fields}
                buttonTextKey={getTranslation(
                    t,
                    TNamespaceEnum.signup,
                    'activate'
                )}
                submit={verifyApplicationContact}
                onSuccess={onMemberApplicationSubmitted}
                goBack={goBack}
                getMutateFieldsCb={getMutateFieldsCb}
            />
        </div>
    );
};

export default SignUpActivation;

const getFieldWithLocalization = (): GroupEditFieldInterface[] => [
    {
        fields: [
            {
                name: EMAIL_ACTIVATION,
                value: '',
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'fields.emailActivation'
                ),
                type: FieldTypeApiEnum.NUMBER,
                size: FilterSize.S25,
                labelSize: LabelSize.TOP,
                required: true,
                hideArrows: true,
                autoFocus: true,
            },
        ],
    },
];
